import { ActionIcon, Badge, Button, Group, Paper, Stack, Text } from '@mantine/core'
import { IconBookmark, IconBookmarkFilled, IconCheck, IconPlus } from '@tabler/icons-react'
import { formatPhone } from '@/utils'
import {
  recipientName,
  rememberKey,
  type FrequentRecipient,
  type ReferralDoctor,
  type ReferralLocation,
  type RosterView,
} from './referralTypes'

function rosterSummary(roster: RosterView, specialtyLabel: string): string {
  if (roster.status === 'pending') return `Checking for ${specialtyLabel} doctors…`
  if (roster.status === 'failed')
    return 'Doctor list unavailable — you can still refer to the practice'
  if (roster.status === 'not-checked') return 'Doctors not checked — open to refer to the practice'
  const n = roster.doctors.length
  return n === 0
    ? `No ${specialtyLabel} doctors listed here — you can refer to the practice`
    : `${n} ${specialtyLabel} doctor${n === 1 ? '' : 's'}`
}

type RememberProps = {
  onRemember: (input: { npi?: string; placeId?: string }) => void
  rememberedKeys: Set<string>
  rememberingKeys: Set<string>
}

function RememberButton({
  name,
  input,
  onRemember,
  rememberedKeys,
  rememberingKeys,
}: RememberProps & { name: string; input: { npi?: string; placeId?: string } }) {
  const key = rememberKey(input)
  if (rememberedKeys.has(key)) {
    // A status, not an action: the same light teal as the referral-count badge, so the
    // row reads "already yours" instead of "a button you cannot press".
    return (
      <Badge
        size="sm"
        variant="light"
        color="teal"
        className="shrink-0"
        leftSection={<IconCheck size={12} />}
        role="status"
        aria-label={`${name} is in frequently used`}
      >
        In frequently used
      </Badge>
    )
  }
  return (
    <Button
      size="xs"
      variant="subtle"
      className="shrink-0"
      leftSection={<IconPlus size={14} />}
      loading={rememberingKeys.has(key)}
      onClick={() => onRemember(input)}
      aria-label={`Add ${name} to frequently used`}
    >
      Add to frequently used
    </Button>
  )
}

function ReferralsBadge({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <Badge size="xs" variant="light" color="teal">
      {count} referral{count === 1 ? '' : 's'}
    </Badge>
  )
}

type ClinicRowProps = {
  location: ReferralLocation
  roster: RosterView
  index: number
  specialtyLabel: string
  expanded: boolean
  onToggle: () => void
  resolvingKey: string | null
  onSelectDoctor: (npi: string) => void
  onSelectPractice: () => void
  rootRef: (el: HTMLDivElement | null) => void
} & RememberProps

export const ClinicRow = ({
  location,
  roster,
  index,
  specialtyLabel,
  expanded,
  onToggle,
  resolvingKey,
  onSelectDoctor,
  onSelectPractice,
  rootRef,
  onRemember,
  rememberedKeys,
  rememberingKeys,
}: ClinicRowProps) => (
  <div ref={rootRef}>
    <Paper withBorder p="sm" mb={6} className={expanded ? 'border-blue-500' : undefined}>
      <Group justify="space-between" wrap="nowrap">
        <div className="min-w-0">
          <Group gap="xs">
            <Text fw={600} size="sm">
              {index}. {location.name}
            </Text>
            <ReferralsBadge count={location.referralCount} />
          </Group>
          <Text size="xs" c="dimmed">
            {location.street}, {location.city}, {location.state} {location.zip} ·{' '}
            {location.distanceMiles.toFixed(1)} mi
          </Text>
          <Text size="xs" c="dimmed">
            {rosterSummary(roster, specialtyLabel)}
          </Text>
        </div>
        {/* shrink-0: a long clinic name must wrap, not squeeze the label (PD-1807). */}
        <Button size="xs" variant="subtle" className="shrink-0" onClick={onToggle}>
          {expanded ? 'Hide' : 'View specialists'}
        </Button>
      </Group>
      {expanded && (
        <Stack gap={4} mt="xs" pl="sm" className="border-l border-gray-200">
          {roster.doctors.map((d) => (
            <Group key={d.npi} justify="space-between" wrap="nowrap">
              <div className="min-w-0">
                <Text size="sm">{recipientName(d)}</Text>
                <Text size="xs" c="dimmed">
                  {d.taxonomyLabel}
                  {d.fax ? ` · Fax ${d.fax}` : ''}
                </Text>
              </div>
              <Group gap="xs" wrap="nowrap" className="shrink-0">
                <RememberButton
                  name={recipientName(d)}
                  input={{ npi: d.npi, placeId: location.placeId }}
                  onRemember={onRemember}
                  rememberedKeys={rememberedKeys}
                  rememberingKeys={rememberingKeys}
                />
                <Button
                  size="xs"
                  variant="light"
                  loading={resolvingKey === `npi:${d.npi}`}
                  onClick={() => onSelectDoctor(d.npi)}
                  aria-label={`Select ${recipientName(d)}`}
                >
                  Select
                </Button>
              </Group>
            </Group>
          ))}
          <Group>
            <Button
              size="xs"
              variant="subtle"
              loading={resolvingKey === `place:${location.placeId}`}
              onClick={onSelectPractice}
            >
              Refer to the practice (no specific doctor)
            </Button>
            <RememberButton
              name={location.name}
              input={{ placeId: location.placeId }}
              onRemember={onRemember}
              rememberedKeys={rememberedKeys}
              rememberingKeys={rememberingKeys}
            />
          </Group>
        </Stack>
      )}
    </Paper>
  </div>
)

type DoctorRowProps = {
  doctor: ReferralDoctor
  busy: boolean
  onSelect: () => void
} & RememberProps

export const DoctorRow = ({
  doctor,
  busy,
  onSelect,
  onRemember,
  rememberedKeys,
  rememberingKeys,
}: DoctorRowProps) => (
  <Paper withBorder p="sm">
    <Group justify="space-between" wrap="nowrap">
      <div className="min-w-0">
        <Group gap="xs">
          <Text fw={600} size="sm">
            {recipientName(doctor)}
          </Text>
          <ReferralsBadge count={doctor.referralCount} />
        </Group>
        <Text size="xs" c="dimmed">
          {doctor.taxonomyLabel}
        </Text>
        <Text size="xs" c="dimmed">
          {doctor.street}, {doctor.city}, {doctor.state} {doctor.zip}
          {doctor.distanceMiles != null ? ` · ${doctor.distanceMiles.toFixed(1)} mi` : ''}
        </Text>
        <Text size="xs" c="dimmed">
          NPI {doctor.npi}
          {doctor.fax ? ` · Fax ${doctor.fax}` : ' · No fax on file'}
        </Text>
      </div>
      <Group gap="xs" wrap="nowrap" className="shrink-0">
        <RememberButton
          name={recipientName(doctor)}
          input={{ npi: doctor.npi }}
          onRemember={onRemember}
          rememberedKeys={rememberedKeys}
          rememberingKeys={rememberingKeys}
        />
        <Button
          size="xs"
          variant="light"
          loading={busy}
          onClick={onSelect}
          aria-label={`Select ${recipientName(doctor)}`}
        >
          Select
        </Button>
      </Group>
    </Group>
  </Paper>
)

type FrequentRowProps = {
  recipient: FrequentRecipient
  busy: boolean
  onSelect: () => void
  onBookmark: (bookmarked: boolean) => void
  /** A bookmark save for this row is in flight; a second click must not race it. */
  bookmarking: boolean
}

export const FrequentRow = ({
  recipient,
  busy,
  onSelect,
  onBookmark,
  bookmarking,
}: FrequentRowProps) => (
  <Paper withBorder p="sm">
    <Group justify="space-between" wrap="nowrap">
      <div className="min-w-0">
        <Group gap="xs">
          <Text fw={600} size="sm">
            {recipient.name}
          </Text>
          <ReferralsBadge count={recipient.referralCount} />
        </Group>
        {recipient.practiceName && recipient.practiceName !== recipient.name && (
          <Text size="xs" c="dimmed">
            {recipient.practiceName}
          </Text>
        )}
        <Text size="xs" c="dimmed">
          {recipient.street}, {recipient.city}, {recipient.state} {recipient.zip}
          {recipient.distanceMiles != null ? ` · ${recipient.distanceMiles.toFixed(1)} mi` : ''}
        </Text>
        <Text size="xs" c="dimmed">
          Fax {formatPhone(recipient.fax)}
        </Text>
      </div>
      <Group gap="xs" wrap="nowrap" className="shrink-0">
        <ActionIcon
          variant="subtle"
          color={recipient.bookmarked ? 'yellow' : 'gray'}
          onClick={() => onBookmark(!recipient.bookmarked)}
          aria-label={
            recipient.bookmarked
              ? `Remove bookmark from ${recipient.name}`
              : `Bookmark ${recipient.name}`
          }
          disabled={bookmarking}
        >
          {recipient.bookmarked ? <IconBookmarkFilled size={16} /> : <IconBookmark size={16} />}
        </ActionIcon>
        <Button
          size="xs"
          variant="light"
          loading={busy}
          onClick={onSelect}
          aria-label={`Select ${recipient.name}`}
        >
          Select
        </Button>
      </Group>
    </Group>
  </Paper>
)
